'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@sharpr/ui';

// Single IntersectionObserver shared across every AnimatedSection instance on
// the landing page — all instances use the same threshold, so one observer
// is enough. A WeakMap holds per-element callbacks.
let sharedObserver: IntersectionObserver | null = null;
const callbacks: WeakMap<Element, () => void> = new WeakMap();

function getSharedObserver(): IntersectionObserver | null {
  if (typeof window === 'undefined') return null;
  if (sharedObserver) return sharedObserver;
  sharedObserver = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        callbacks.get(entry.target)?.();
        obs.unobserve(entry.target);
        callbacks.delete(entry.target);
      }
    },
    // Negative bottom margin so a section fades only once its top crosses ~80%
    // of the viewport, not at page load.
    { threshold: 0.1, rootMargin: '0px 0px -20% 0px' },
  );
  return sharedObserver;
}

export function AnimatedSection({
  children,
  className,
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Marker class deliberately NOT `animate-in` — tailwindcss-animate defines
    // that as a CSS animation that would suppress the opacity/transform transition.

    // Sections already in/above the viewport at mount fade in right away (the
    // shrunk rootMargin would leave them invisible). Double rAF so the opacity-0
    // frame paints first and the transition runs.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => el.classList.add('section-in-view'));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    const obs = getSharedObserver();
    if (!obs) return;
    callbacks.set(el, () => el.classList.add('section-in-view'));
    obs.observe(el);
    return () => {
      obs.unobserve(el);
      callbacks.delete(el);
    };
  }, []);

  return (
    <div
      ref={ref}
      id={id}
      className={cn(
        'opacity-0 translate-y-6 transition-[opacity,transform] duration-700 ease-out',
        '[&.section-in-view]:opacity-100 [&.section-in-view]:translate-y-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
